import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, ExternalLink } from "lucide-react";
import { listarFacturas, descargarFacturaPDF } from "@/lib/facturas.functions";

export const Route = createFileRoute("/_authenticated/gestion/facturas")({
  component: FacturasPage,
});

type Factura = {
  id: string;
  reserva_id: string;
  numero: number;
  numero_completo: string | null;
  fecha_emision: string;
  base_imponible: number;
  iva_importe: number;
  total: number;
  cliente_snapshot: { nombre?: string; email?: string } | null;
  anulada: boolean;
};

function firstDayOfYear() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

function FacturasPage() {
  const listar = useServerFn(listarFacturas);
  const descargar = useServerFn(descargarFacturaPDF);
  const [desde, setDesde] = useState(firstDayOfYear());
  const [hasta, setHasta] = useState(today());
  const [rows, setRows] = useState<Factura[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listar({ data: { desde, hasta } });
      setRows(r as Factura[]);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [listar, desde, hasta]);

  useEffect(() => { load(); }, [load]);

  async function handleDescargar(reserva_id: string) {
    try {
      const { filename, base64 } = await descargar({ data: { reserva_id } });
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error((e as Error).message); }
  }

  const totales = rows.reduce(
    (acc, r) => {
      if (r.anulada) return acc;
      acc.base += Number(r.base_imponible);
      acc.iva += Number(r.iva_importe);
      acc.total += Number(r.total);
      return acc;
    },
    { base: 0, iva: 0, total: 0 },
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl">Facturación</h1>
        <p className="text-sm text-muted-foreground">
          Facturas emitidas correlativamente. Se generan desde cada reserva con el botón "Emitir factura".
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div><Label>Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
          <div><Label>Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
          <Button onClick={load} disabled={loading}>Actualizar</Button>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Base imponible</p><p className="text-xl font-medium">{totales.base.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">IVA repercutido</p><p className="text-xl font-medium">{totales.iva.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total facturado</p><p className="text-xl font-medium">{totales.total.toFixed(2)} €</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Facturas ({rows.length})</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay facturas en el rango seleccionado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-2">Nº</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th className="text-right">Base</th>
                    <th className="text-right">IVA</th>
                    <th className="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-t ${r.anulada ? "text-muted-foreground line-through" : ""}`}>
                      <td className="py-2 font-medium">{r.numero_completo ?? r.numero}</td>
                      <td>{r.fecha_emision}</td>
                      <td>{r.cliente_snapshot?.nombre ?? "—"}</td>
                      <td className="text-right">{Number(r.base_imponible).toFixed(2)} €</td>
                      <td className="text-right">{Number(r.iva_importe).toFixed(2)} €</td>
                      <td className="text-right font-medium">{Number(r.total).toFixed(2)} €</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleDescargar(r.reserva_id)} title="Descargar PDF">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button asChild size="icon" variant="ghost" title="Ver reserva">
                            <Link to="/gestion/reservas/$id" params={{ id: r.reserva_id }}><ExternalLink className="h-4 w-4" /></Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}